import React, { memo, useCallback } from 'react';
import {
  Button,
  InputAdornment,
  InputBase,
  MenuItem,
  Select
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { useDebouncedCallback } from 'use-debounce';
import { useMessage, useMessages } from 'modules/intl';
import { useConnectorSelectionContext } from './contexts/ConnectorSelectionContextProvide';

const useStyles = makeStyles((theme) => ({
  input: {
    backgroundColor: theme.palette.grey[300],
    marginRight: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    '& .MuiInputBase-input::placeholder': {
      opacity: 0.9,
      color: '#424242'
    },
    color: '#424242',
    fontSize: theme.typography.body2.fontSize
  },
  iconSearch: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-1)
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(4.5),
    margin: theme.spacing(2, 2, 0, 2)
  },
  select: {
    backgroundColor: theme.palette.common.white,
    width: theme.spacing(25)
  },
  menuItem: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.grey[200]!,
      '&:hover': {
        backgroundColor: theme.palette.grey[100]!
      }
    }
  },
  clearIcon: {
    color: theme.palette.grey[600]
  }
}));

type ConnectorHeaderProps = {
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
};

const selectMessages = [
  'connectors.all',
  'connectors.installed',
  'connectors.notInstalled'
];

export const ConnectorHeader: React.FC<ConnectorHeaderProps> = memo(
  ({ setSearchValue }: ConnectorHeaderProps) => {
    const classes = useStyles();
    const selectValues = useMessages(selectMessages);
    const {
      connectorsSelectionStatus: connectorsSelectionStatus,
      setConnectorsSelectionStatus: setConnectorsSelectionStatus
    } = useConnectorSelectionContext();

    const onSelectChange = useCallback(
      (event) => {
        setConnectorsSelectionStatus(event.target.value);
      },
      [setConnectorsSelectionStatus]
    );
    const searchText = useMessage('connectors.search');

    const [search, setSearch] = React.useState('');

    const onSearch = useDebouncedCallback((input) => {
      setSearchValue(input);
    }, 1000);

    const handleSearch = useCallback(
      (event) => {
        setSearch(event.target.value || '');
        onSearch(event.target.value || '');
      },
      [onSearch]
    );

    const handleClearSearch = useCallback(
      (event) => {
        onSearch(event.target.value || '');
        setSearch(event.target.value || '');
      },
      [onSearch]
    );

    return (
      <div className={classes.topContainer} data-testid="connectorAllPage">
        <InputBase
          className={classes.input}
          placeholder={searchText}
          inputProps={{
            'aria-label': searchText,
            'data-testid': 'search-connector'
          }}
          value={search}
          startAdornment={
            <InputAdornment position="start">
              <SearchSharpIcon
                className={classes.iconSearch}
                data-testid="search-sharp-icon"
              />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              {search.length > 0 && (
                <Button onClick={handleClearSearch} data-testid="remove-search">
                  <ClearSharpIcon className={classes.clearIcon} />
                </Button>
              )}
            </InputAdornment>
          }
          fullWidth
          data-testid="connectorSearch"
          onChange={handleSearch}
        />
        <Select
          id="connector-select"
          value={connectorsSelectionStatus}
          className={classes.select}
          role="combobox"
          onChange={onSelectChange}
          data-testid="connectorSelect"
        >
          {Object.keys(selectValues).map((i) => {
            return (
              <MenuItem
                value={i}
                key={i}
                className={classes.menuItem}
                data-testid={`connectorSelectOption-${i}`}
              >
                {selectValues[i]}
              </MenuItem>
            );
          })}
        </Select>
      </div>
    );
  }
);
ConnectorHeader.displayName = 'ConnectorHeader';
